import type { CertificateOrientation } from '@zeus/index'
import { Eye, Loader } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import SettingToggleCard from '~/components/SettingToggleCard'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { tourAnchor } from '~/helpers/tour/anchors'
import { PLACEHOLDERS } from './certificate-form'
import CertificateLayoutFields from './CertificateLayoutFields'
import { useCertificateForm } from './use-certificate-form'

type Props = {
  /** Le modèle édité (#162). */
  templateId: string
  /** Après un enregistrement : la liste des modèles peut avoir changé de nom. */
  onSaved: () => Promise<unknown>
  /** Le formulaire diffère, ou plus, de ce qui est enregistré. */
  onDirtyChange: (dirty: boolean) => void
}

function CertificateSettings({ templateId, onSaved, onDirtyChange }: Props) {
  const { t } = useTranslation()
  const { form, set, loading, saving, previewing, skeletonActive, dirty, removeSkeleton, handleLogoFile, handleSkeletonFile, handleSubmit, handlePreview }
    = useCertificateForm(templateId, onSaved)

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange])

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      <Card {...tourAnchor('certificates.template')}>
        <CardHeader>
          <CardTitle>{t('certificates.template.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certName" required>{t('certificates.field.name')}</Label>
            <Input id="certName" value={form.name} onChange={e => set('name')(e.target.value)} aria-describedby="certNameHelp" required />
            <p id="certNameHelp" className="text-xs text-muted-foreground">{t('certificates.field.nameHelp')}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certTitle">{t('certificates.field.title')}</Label>
            <Input
              id="certTitle"
              value={form.title}
              onChange={e => set('title')(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certBody">{t('certificates.field.body')}</Label>
            <textarea
              id="certBody"
              value={form.body}
              onChange={e => set('body')(e.target.value)}
              rows={5}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm"
            />
            <p className="text-xs text-muted-foreground" {...tourAnchor('certificates.variables')}>
              {t('certificates.placeholders.help')}
              {' '}
              {PLACEHOLDERS.join(' ')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certSignature">{t('certificates.field.signature')}</Label>
            <Input
              id="certSignature"
              value={form.signature}
              onChange={e => set('signature')(e.target.value)}
              placeholder={t('certificates.field.signaturePlaceholder')}
            />
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.showScore}
                onChange={e => set('showScore')(e.target.checked)}
              />
              {t('certificates.field.showScore')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.showDate}
                onChange={e => set('showDate')(e.target.checked)}
              />
              {t('certificates.field.showDate')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.showTotalTime}
                onChange={e => set('showTotalTime')(e.target.checked)}
              />
              {t('certificates.field.showTotalTime')}
            </label>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="certOrientation">{t('certificates.field.orientation')}</Label>
            <select
              id="certOrientation"
              value={form.orientation}
              onChange={e => set('orientation')(e.target.value as CertificateOrientation)}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              <option value="LANDSCAPE">{t('certificates.orientation.landscape')}</option>
              <option value="PORTRAIT">{t('certificates.orientation.portrait')}</option>
            </select>
          </div>

          <CertificateLayoutFields
            form={form}
            set={set}
            skeletonActive={skeletonActive}
            onLogoFile={handleLogoFile}
            onSkeletonFile={handleSkeletonFile}
            onRemoveSkeleton={removeSkeleton}
          />
        </CardContent>
      </Card>

      <SettingToggleCard
        title={t('certificates.email.title')}
        label={t('certificates.field.emailOnUnlock')}
        help={t('certificates.email.help')}
        checked={form.emailOnUnlock}
        onCheckedChange={set('emailOnUnlock')}
        {...tourAnchor('certificates.email')}
      />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={handlePreview} disabled={previewing} {...tourAnchor('certificates.preview')}>
          {previewing
            ? <Loader className="mr-2 size-4 animate-spin" />
            : <Eye className="mr-2 size-4" />}
          {t('certificates.preview')}
        </Button>
        <Button type="submit" disabled={saving} {...tourAnchor('certificates.save')}>
          {saving && <Loader className="mr-2 size-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export default CertificateSettings
